import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { SidebarProvider, useSidebar } from "../../../components/ui/Sidebar";
import Sidebar from "../../../components/ui/Sidebar";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useGetExpenses } from "../expenses/useGetExpenses";

// ── Category colour palette ───────────────────────────────────────────────────
const FALLBACK_COLOR = {
  bg: "bg-slate-100 dark:bg-slate-800/40",
  text: "text-slate-700 dark:text-slate-300",
  bar: "bg-slate-400",
  pie: "#94a3b8",
};

const CATEGORY_COLORS = {
  Administrative: {
    bg: "bg-blue-100 dark:bg-blue-900/30",
    text: "text-blue-700 dark:text-blue-300",
    bar: "bg-blue-500",
    pie: "#3b82f6",
  },
  Advertisement: {
    bg: "bg-orange-100 dark:bg-orange-900/30",
    text: "text-orange-700 dark:text-orange-300",
    bar: "bg-orange-500",
    pie: "#f97316",
  },
  Fees: {
    bg: "bg-violet-100 dark:bg-violet-900/30",
    text: "text-violet-700 dark:text-violet-300",
    bar: "bg-violet-500",
    pie: "#8b5cf6",
  },
  "Office Expense": {
    bg: "bg-teal-100 dark:bg-teal-900/30",
    text: "text-teal-700 dark:text-teal-300",
    bar: "bg-teal-500",
    pie: "#14b8a6",
  },
  Rent: {
    bg: "bg-red-100 dark:bg-red-900/30",
    text: "text-red-700 dark:text-red-300",
    bar: "bg-red-500",
    pie: "#ef4444",
  },
  Salary: {
    bg: "bg-green-100 dark:bg-green-900/30",
    text: "text-green-700 dark:text-green-300",
    bar: "bg-green-500",
    pie: "#22c55e",
  },
  "Vendor Expense": {
    bg: "bg-pink-100 dark:bg-pink-900/30",
    text: "text-pink-700 dark:text-pink-300",
    bar: "bg-pink-500",
    pie: "#ec4899",
  },
};

// ── Custom Pie tooltip ────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-white dark:bg-gray-800 border border-border rounded-xl shadow-lg px-4 py-3 text-sm">
      <p className="font-semibold text-foreground mb-1">{d.name}</p>
      <p className="text-muted-foreground">
        SR {d.value.toLocaleString("en-SA", { minimumFractionDigits: 2 })}
      </p>
      <p className="text-muted-foreground">{d.payload.percentage}%</p>
    </div>
  );
};

// ── Custom Pie label ──────────────────────────────────────────────────────────
const renderCustomLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percentage,
}) => {
  if (percentage < 5) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={11}
      fontWeight={600}
    >
      {`${percentage}%`}
    </text>
  );
};

// ── Dashboard Content ─────────────────────────────────────────────────────────
const PurchasesDashboardContent = () => {
  const { isCollapsed } = useSidebar();
  const { t } = useTranslation("pages");

  const { data: expenses = [] } = useGetExpenses();

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [vendorSearch, setVendorSearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [allSelected, setAllSelected] = useState(true);

  // Categories present in the live data (falls back to the known palette list).
  const CATEGORIES = useMemo(() => {
    const present = [
      ...new Set(expenses.map((e) => e.category).filter(Boolean)),
    ];
    return present.sort((a, b) => a.localeCompare(b));
  }, [expenses]);

  // ── Filter logic ────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return expenses.filter((e) => {
      if (e.status === "void") return false;
      const date = new Date(e.date);
      const fromOk = !dateFrom || date >= new Date(dateFrom);
      const toOk = !dateTo || date <= new Date(dateTo);
      const vendorOk =
        !vendorSearch.trim() ||
        (e.vendorName || "").toLowerCase().includes(vendorSearch.toLowerCase());
      const catOk =
        allSelected ||
        selectedCategories.length === 0 ||
        selectedCategories.includes(e.category);
      return fromOk && toOk && vendorOk && catOk;
    });
  }, [
    expenses,
    dateFrom,
    dateTo,
    vendorSearch,
    selectedCategories,
    allSelected,
  ]);

  // ── Category distribution ───────────────────────────────────────────────────
  const distribution = useMemo(() => {
    const totalAmount = filtered.reduce((s, e) => s + e.amount, 0);
    return CATEGORIES.map((cat) => {
      const rows = filtered.filter((e) => e.category === cat);
      const total = rows.reduce((s, e) => s + e.amount, 0);
      const pct = totalAmount > 0 ? Math.round((total / totalAmount) * 100) : 0;
      return { category: cat, count: rows.length, total, percentage: pct };
    }).filter((r) => r.count > 0);
  }, [filtered]);

  const grandTotal = filtered.reduce((s, e) => s + e.amount, 0);
  const avgAmount = filtered.length > 0 ? grandTotal / filtered.length : 0;

  const pieData = distribution.map((d) => ({
    name: d.category,
    value: d.total,
    percentage: d.percentage,
    color: CATEGORY_COLORS[d.category]?.pie || FALLBACK_COLOR.pie,
  }));

  // ── Category toggle ─────────────────────────────────────────────────────────
  const toggleCategory = (cat) => {
    if (cat === "__all__") {
      setAllSelected(true);
      setSelectedCategories([]);
      return;
    }
    setAllSelected(false);
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat],
    );
  };

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setVendorSearch("");
    setSelectedCategories([]);
    setAllSelected(true);
  };

  const hasFilters = dateFrom || dateTo || vendorSearch || !allSelected;

  const fmt = (n) =>
    (n || 0).toLocaleString("en-SA", { minimumFractionDigits: 2 });

  return (
    <div
      className={`transition-all duration-300 ease-in-out ${isCollapsed ? "md:ms-16" : "md:ms-64"}`}
    >
      <main className="p-4 md:p-6">
        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-1">
                {t("purchasesDashboard.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("purchasesDashboard.description")}
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" size="md" iconName="Download">
                {t("purchasesDashboard.exportReport")}
              </Button>
              {hasFilters && (
                <Button
                  variant="outline"
                  size="md"
                  iconName="X"
                  onClick={clearFilters}
                >
                  {t("actions.clear")} {t("actions.filters")}
                </Button>
              )}
            </div>
          </div>
        </motion.div>

        {/* ── Filters ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-6"
        >
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon
                name="SlidersHorizontal"
                size={16}
                className="text-primary"
              />
            </div>
            <h2 className="text-sm font-semibold text-foreground dark:text-white uppercase tracking-wide">
              {t("actions.filters")}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            {/* Date From */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">
                From Date
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">
                To Date
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Vendor Search */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">
                {t("purchasesDashboard.vendor")}
              </label>
              <div className="relative">
                <Icon
                  name="Search"
                  size={15}
                  className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  type="text"
                  value={vendorSearch}
                  onChange={(e) => setVendorSearch(e.target.value)}
                  placeholder={t("purchasesDashboard.searchVendor")}
                  className="w-full ps-9 pe-9 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                {vendorSearch && (
                  <button
                    onClick={() => setVendorSearch("")}
                    className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <Icon name="X" size={13} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Category checkboxes */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">
              Category
            </label>
            <div className="flex flex-wrap gap-2">
              {/* Select All */}
              <button
                onClick={() => toggleCategory("__all__")}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-all duration-150 ${
                  allSelected
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-gray-200 dark:border-gray-600 text-muted-foreground hover:border-primary/40 hover:bg-muted/50"
                }`}
              >
                <div
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    allSelected
                      ? "border-primary bg-primary"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {allSelected && (
                    <Icon name="Check" size={10} className="text-white" />
                  )}
                </div>
                Select All
              </button>

              {CATEGORIES.map((cat) => {
                const active = !allSelected && selectedCategories.includes(cat);
                const colors = CATEGORY_COLORS[cat] || FALLBACK_COLOR;
                return (
                  <button
                    key={cat}
                    onClick={() => toggleCategory(cat)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-all duration-150 ${
                      active
                        ? `border-current ${colors.bg} ${colors.text}`
                        : "border-gray-200 dark:border-gray-600 text-muted-foreground hover:border-gray-300 hover:bg-muted/50"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        active
                          ? "border-current bg-current"
                          : "border-gray-300 dark:border-gray-600"
                      }`}
                    >
                      {active && (
                        <Icon name="Check" size={10} className="text-white" />
                      )}
                    </div>
                    {cat}
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* ── Stats Row ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
        >
          {[
            {
              label: t("purchasesDashboard.stats.totalExpenses"),
              value: filtered.length,
              sub: "transactions",
              icon: "ReceiptText",
              color:
                "text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400",
              delay: 0,
            },
            {
              label: "Total Amount",
              value: `SR ${fmt(grandTotal)}`,
              sub: "across all categories",
              icon: "DollarSign",
              color:
                "text-red-500 bg-red-100 dark:bg-red-900/20 dark:text-red-400",
              delay: 0.04,
            },
            {
              label: "Avg. Expense Amount",
              value: `SR ${fmt(avgAmount)}`,
              sub: "per transaction",
              icon: "TrendingUp",
              color:
                "text-violet-600 bg-violet-100 dark:bg-violet-900/20 dark:text-violet-400",
              delay: 0.08,
            },
          ].map((stat) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 + stat.delay }}
              className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 flex items-center gap-4"
            >
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${stat.color}`}
              >
                <Icon name={stat.icon} size={22} />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground dark:text-white">
                  {stat.value}
                </p>
                <p className="text-xs font-medium text-muted-foreground dark:text-gray-400">
                  {stat.label}
                </p>
                <p className="text-xs text-muted-foreground/60 dark:text-gray-500">
                  {stat.sub}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* ── Main content: Table + Pie ── */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* ── Distribution Table (3/5) ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="lg:col-span-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
          >
            <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="BarChart3" size={16} className="text-primary" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-foreground dark:text-white">
                  Expense Distribution by Category
                </h2>
                <p className="text-xs text-muted-foreground dark:text-gray-400">
                  Breakdown of spending across all categories
                </p>
              </div>
            </div>

            {distribution.length === 0 ? (
              <div className="p-12 text-center">
                <Icon
                  name="BarChart3"
                  size={40}
                  className="mx-auto text-muted-foreground/30 mb-3"
                />
                <p className="text-muted-foreground text-sm">
                  {t("purchasesDashboard.emptyFiltered")}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-6 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Category
                      </th>
                      <th className="px-6 py-3.5 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Transactions
                      </th>
                      <th className="px-6 py-3.5 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Total Amount (SR)
                      </th>
                      <th className="px-6 py-3.5 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        % Share
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {distribution.map((row, index) => {
                      const colors =
                        CATEGORY_COLORS[row.category] || FALLBACK_COLOR;
                      return (
                        <motion.tr
                          key={row.category}
                          initial={{ opacity: 0, x: -12 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-3 h-3 rounded-full flex-shrink-0`}
                                style={{
                                  backgroundColor: colors.pie || "#94a3b8",
                                }}
                              />
                              <span
                                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}
                              >
                                {row.category}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-muted-foreground dark:text-gray-400">
                            {row.count}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <span className="text-sm font-semibold text-foreground dark:text-white">
                              {fmt(row.total)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-end gap-3">
                              {/* Mini bar */}
                              <div className="w-20 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden hidden md:block">
                                <motion.div
                                  initial={{ width: 0 }}
                                  animate={{ width: `${row.percentage}%` }}
                                  transition={{
                                    duration: 0.6,
                                    delay: index * 0.05 + 0.2,
                                  }}
                                  className={`h-full rounded-full ${colors.bar || "bg-gray-400"}`}
                                />
                              </div>
                              <span className="text-sm font-semibold text-foreground dark:text-white w-10 text-right">
                                {row.percentage}%
                              </span>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>

                  {/* Totals row */}
                  <tfoot className="bg-gray-50 dark:bg-gray-700/50 border-t-2 border-gray-200 dark:border-gray-600">
                    <tr>
                      <td className="px-6 py-4">
                        <span className="text-sm font-bold text-foreground dark:text-white">
                          Total
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm font-bold text-foreground dark:text-white">
                          {filtered.length}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm font-bold text-foreground dark:text-white">
                          SR {fmt(grandTotal)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm font-bold text-foreground dark:text-white">
                          100%
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </motion.div>

          {/* ── Pie Chart (2/5) ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 flex flex-col"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="PieChart" size={16} className="text-primary" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-foreground dark:text-white">
                  Expense Breakdown
                </h2>
                <p className="text-xs text-muted-foreground dark:text-gray-400">
                  By category share
                </p>
              </div>
            </div>

            {pieData.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
                <Icon
                  name="PieChart"
                  size={40}
                  className="text-muted-foreground/30 mb-3"
                />
                <p className="text-muted-foreground text-sm">
                  No data to display
                </p>
              </div>
            ) : (
              <div className="flex-1 flex flex-col">
                {/* Pie */}
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        labelLine={false}
                        label={renderCustomLabel}
                      >
                        {pieData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            stroke="none"
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend */}
                <div className="mt-4 space-y-2.5">
                  {pieData.map((entry) => (
                    <div
                      key={entry.name}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: entry.color }}
                        />
                        <span className="text-sm text-muted-foreground dark:text-gray-400 truncate">
                          {entry.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                        <span className="text-xs text-muted-foreground dark:text-gray-500">
                          SR{" "}
                          {entry.value.toLocaleString("en-SA", {
                            maximumFractionDigits: 0,
                          })}
                        </span>
                        <span className="text-xs font-semibold text-foreground dark:text-white w-9 text-right">
                          {entry.percentage}%
                        </span>
                      </div>
                    </div>
                  ))}

                  {/* Total line */}
                  <div className="pt-2.5 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground dark:text-white">
                      Total
                    </span>
                    <span className="text-sm font-bold text-primary">
                      SR{" "}
                      {grandTotal.toLocaleString("en-SA", {
                        maximumFractionDigits: 0,
                      })}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* ── Active filters summary ── */}
        <AnimatePresence>
          {hasFilters && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-6 flex flex-wrap gap-2 items-center"
            >
              <span className="text-xs text-muted-foreground font-medium">
                Active filters:
              </span>
              {dateFrom && (
                <span className="flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                  From: {dateFrom}
                  <button
                    onClick={() => setDateFrom("")}
                    className="hover:text-primary/70"
                  >
                    <Icon name="X" size={10} />
                  </button>
                </span>
              )}
              {dateTo && (
                <span className="flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                  To: {dateTo}
                  <button
                    onClick={() => setDateTo("")}
                    className="hover:text-primary/70"
                  >
                    <Icon name="X" size={10} />
                  </button>
                </span>
              )}
              {vendorSearch && (
                <span className="flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                  {t("purchasesDashboard.vendor")}: {vendorSearch}
                  <button
                    onClick={() => setVendorSearch("")}
                    className="hover:text-primary/70"
                  >
                    <Icon name="X" size={10} />
                  </button>
                </span>
              )}
              {!allSelected &&
                selectedCategories.map((cat) => (
                  <span
                    key={cat}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${(CATEGORY_COLORS[cat] || FALLBACK_COLOR).bg} ${(CATEGORY_COLORS[cat] || FALLBACK_COLOR).text}`}
                  >
                    {cat}
                    <button
                      onClick={() => toggleCategory(cat)}
                      className="hover:opacity-70"
                    >
                      <Icon name="X" size={10} />
                    </button>
                  </span>
                ))}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="pb-8" />
      </main>
    </div>
  );
};

const PurchasesDashboard = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <PurchasesDashboardContent />
    </div>
  </SidebarProvider>
);

export default PurchasesDashboard;
